// getPag.js
'use strict';

import { LRUCache } from 'lru-cache';

import { getHeaders } from '../resource/fetchOptionHeader.js';
import responseDecryptionUtil from '../resource/responseDecryptionUtil.js';
import env from '../resource/env.js';

let CACHED_PAG_MODULE = null;

const cache = new LRUCache({
  max: 20,
  dispose: pagFile => {
    pagFile?.destroy?.();
  },
});

/**
 * get Pag binary data
 * @param {string} imageSource - PAG file URL
 */
async function fetchPagBinary(imageSource) {
  const isPublicAsset = imageSource?.includes(env.PUBLIC_URL_PREFIX);
  const fetchOptions = { method: 'GET', headers: getHeaders() };

  if (isPublicAsset && env.FEATURE_RESOURCE_DECRYPTION) {
    const encryptedUrl = imageSource.replace(
      env.PUBLIC_URL_PREFIX,
      env.PUBLIC_ENCRYPTED_URL_PREFIX
    );
    const response = await fetch(encryptedUrl, fetchOptions);
    const decryptedData = await responseDecryptionUtil({ response });
    return decryptedData.buffer;
  }

  const response = await fetch(imageSource, isPublicAsset ? fetchOptions : {});
  const buffer = await response.arrayBuffer();
  return buffer;
}

/**
 * initialize PAG module
 */
async function initializePagModule() {
  if (CACHED_PAG_MODULE) return CACHED_PAG_MODULE;

  const { PAGInit } = await import('libpag');
  // Configure WASM file location for Next.js
  let wasmUrl;
  try {
    const wasmModule = await import('libpag/lib/libpag.wasm');
    wasmUrl = wasmModule?.default || wasmModule;
  } catch (_) {
    // Fallback if direct import fails
  }

  CACHED_PAG_MODULE = await PAGInit({
    locateFile: fileName => {
      if (fileName.endsWith('.wasm') && wasmUrl) {
        return wasmUrl;
      }
      // Try to construct path based on current location
      try {
        if (wasmUrl) {
          const url = new URL(wasmUrl, window.location.origin);
          const directoryPath = url.pathname.replace(/[^/]*$/, '');
          return `${directoryPath}${fileName}`;
        }
      } catch (_) {
        // Fallback to default
      }
      return fileName;
    },
  });
  return CACHED_PAG_MODULE;
}

/**
 * create PAG player
 */
function createPagPlayer(pagFile, pagModule, duration) {
  return async canvasElement => {
    if (!canvasElement) return null;

    const pagView = await pagModule.PAGView.init(pagFile, canvasElement);
    let isDisposed = false;

    // set scale mode
    try {
      const scaleMode = pagModule.ScaleMode?.Zoom ?? 2;
      pagView.setScaleMode(scaleMode);
    } catch (_) {
      // ignore API difference
    }

    const safeCall = (fn, ...args) => {
      if (isDisposed) return;
      try {
        return fn.apply(pagView, args);
      } catch (_) {
        // ignore WASM error
      }
    };

    return {
      _view: pagView,
      isDisposed: () => isDisposed,
      play: () => safeCall(pagView.play),
      stop: () => safeCall(pagView.stop),
      isPlaying: () => safeCall(pagView.isPlaying) || false,
      setRepeatCount: count => safeCall(pagView.setRepeatCount, count),
      getDuration: () => duration,
      dispose: () => {
        if (isDisposed) return;
        isDisposed = true;

        // Delay destruction to prevent WASM memory errors
        // Ensures all pending render frames and WASM operations complete before resource cleanup
        // Immediate destruction during animation playback may cause "memory access out of bounds"
        setTimeout(() => {
          pagView.destroy();
        }, 50);
      },
    };
  };
}

/**
 * Check if PAG is in cache (without fetching)
 * @param {string} imageSource - PAG file URL
 * @return {boolean} true if PAG is cached
 */
export function isPagCached(imageSource) {
  if (!imageSource || typeof window === 'undefined') {
    return false;
  }
  return cache.has(imageSource);
}

/**
 * get PAG animation resource
 * @param {Object} params - parameters object
 * @param {string} [params.imageSource] - PAG file URL
 * @return {Promise<Object>} PAG object
 */
async function getPag({ imageSource } = {}) {
  if (!imageSource) {
    return { isPAGSupported: false, pag: null };
  }

  try {
    // check browser environment
    if (typeof window === 'undefined') {
      return { isPAGSupported: false, pag: null };
    }

    // check cache
    const cached = cache.get(imageSource);
    if (cached) {
      return {
        isPAGSupported: true,
        pag: {
          file: cached,
          width: cached.width(),
          height: cached.height(),
          duration: cached.duration(),
          getPlayer: createPagPlayer(
            cached,
            await initializePagModule(),
            cached.duration()
          ),
        },
      };
    }

    // get binary data
    const binaryData = await fetchPagBinary(imageSource);

    if (!binaryData?.byteLength) {
      return { isPAGSupported: false, pag: null };
    }

    // initialize PAG module and load file
    const pagModule = await initializePagModule();
    const pagFile = await pagModule.PAGFile.load(binaryData);

    // add to cache
    cache.set(imageSource, pagFile);

    return {
      isPAGSupported: true,
      pag: {
        file: pagFile,
        width: pagFile.width(),
        height: pagFile.height(),
        duration: pagFile.duration(),
        getPlayer: createPagPlayer(pagFile, pagModule, pagFile.duration()),
      },
    };
  } catch (error) {
    return { isPAGSupported: false, pag: null };
  }
}

export default getPag;
